<template>
  <PbDialog
    v-if="presets && presets.length"
    :dialog-title="translate($route.meta.title)"
    :loading="loading"
    :close-to="closeTo"
  >
    <Radio
      v-model="distributionType"
      :options="distributionOptions"
    />
    <PbInput
      v-if="showDistributionInput"
      v-model="distributionInput"
      dynamic
      inputmode="numeric"
      :label="distributionType.title"
      :placeholder="distributionType.placeholder"
      class="pb-half-margin-bottom"
      autoselect
    >
      <i
        v-tooltip="{ zindex: 9999, text: translate('pb2.distributePresetsView.inputHelpTooltip') }"
        class="icon-info pb-presets-distribution-input-info"
      />
    </PbInput>
    <div
      v-if="showDistributionInput && distributionInput && errors"
      class="msg txt-error pb-presets-distribution-errors"
    >
      <ul class="pb-presets-distribution-errors-list">
        <li
          v-for="error in errors"
          :key="error"
        >
          {{ error }}
        </li>
      </ul>
    </div>
    <DialogActions
      :loading="loading"
      :cancel-attrs="{ to: closeTo }"
      :confirm-disabled="disabled"
      confirm-text="pb2.distributePresetsView.distributeLabel"
      @confirm="distribute"
    />
  </PbDialog>
</template>

<script setup>
import { Tools } from '@nicolaudie-group/ls-utilities'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'
import useValidator from '@nicolaudie-group/ng-ui-kit/composables/useValidator'
import Radio from '@nicolaudie-group/ng-ui-kit/components/Radio/Radio.vue'
import usePresetRangeStore from '@/stores/presetRange.store'
import { computed, ref, watch } from 'vue'
import PbDialog from '@/components/PbDialog.vue'
import useIsGlobal from '@/composables/useIsGlobal'
import useRouterStore from '@/stores/router.store'
import useProfileStore from '@/stores/profile.store'
import routeNames from '@/router/names'
import useTranslate from '@/composables/useTranslate'
import { usePresets, useObject, usePresetRangeStatus } from '@/composables/useProfile'
import DialogActions from '@/components/DialogActions/DialogActions.vue'
import PbInput from '@/components/PbInput.vue'

const routerStore = useRouterStore()
const presetRangeStore = usePresetRangeStore()
const profileStore = useProfileStore()
const { translate } = useTranslate()

const TYPE_SPREAD = 'spread'
const TYPE_SIZE = 'size'
const TYPE_START = 'start'
const TYPE_END = 'end'
const CUSTOM_TYPES = [TYPE_SIZE, TYPE_START, TYPE_END]

const presets = usePresets()
const distributionType = ref({ id: TYPE_SPREAD }) // auto, size, start, end

const showDistributionInput = computed(() => (
  CUSTOM_TYPES.includes(distributionType.value.id)
))

const defaultDistributions = computed(() => {
  const result = {
    [TYPE_START]: [],
    [TYPE_SIZE]: [],
    [TYPE_END]: [],
  }
  const presetsList = presets.value
  if (presetsList) {
    for (let i = 0; i < presetsList.length; i += 1) {
      const preset = presetsList[i].data
      const start = preset.dmx_start
      const size = (preset.dmx_end - preset.dmx_start) + 1
      const end = preset.dmx_end
      result[TYPE_START].push(start)
      result[TYPE_SIZE].push(size)
      result[TYPE_END].push(end)
    }
  }
  return result
})

function join(list = []) {
  return list.join(', ')
}

function summarizeList(list = [], maxItems = 7) {
  if (list.length > maxItems) {
    const ellipsisStart = Math.floor((maxItems - 1) / 2)
    const ellipsisEnd = -Math.ceil((maxItems - 1) / 2)
    return `${join(list.slice(0, ellipsisStart))}, ... , ${join(list.slice(ellipsisEnd))}`
  }
  return join(list)
}

const distributionOptions = computed(() => {
  const values = defaultDistributions.value
  const placeholders = {
    [TYPE_START]: summarizeList(values[TYPE_START]),
    [TYPE_SIZE]: summarizeList(values[TYPE_SIZE]),
    [TYPE_END]: summarizeList(values[TYPE_END]),
  }
  return [
    {
      id: TYPE_SPREAD,
      title: translate('pb2.distributePresetsView.spreadOptionTitle'),
      description: translate('pb2.distributePresetsView.spreadOptionDescription'),
    },
    {
      id: TYPE_START,
      title: translate('pb2.distributePresetsView.startValuesOptionTitle'),
      description: `${translate('pb2.distributePresetsView.startValuesOptionDescription')} (${placeholders[TYPE_START]})`,
      placeholder: placeholders[TYPE_START],
    },
    {
      id: TYPE_SIZE,
      title: translate('pb2.distributePresetsView.sizesOptionTitle'),
      description: `${translate('pb2.distributePresetsView.sizesOptionDescription')} (${placeholders[TYPE_SIZE]})`,
      placeholder: placeholders[TYPE_SIZE],
    },
    {
      id: TYPE_END,
      title: translate('pb2.distributePresetsView.endValuesOptionTitle'),
      description: `${translate('pb2.distributePresetsView.endValuesOptionDescription')} (${placeholders[TYPE_END]})`,
      placeholder: placeholders[TYPE_END],
    },
  ]
})

const distributionInput = ref('')

watch([defaultDistributions, distributionType], (
  [newDefaultDistributions, newDistributionType],
  [oldDefaultDistributions, oldDistributionType],
) => {
  const { value } = distributionInput
  if (
    !oldDefaultDistributions
    || !oldDistributionType
    || !value
    || value === join(oldDefaultDistributions[oldDistributionType.id])
  ) {
    if (newDefaultDistributions[newDistributionType.id]) {
      distributionInput.value = join(newDefaultDistributions[newDistributionType.id])
    }
  }
}, { immediate: true })

const distributionValues = computed(() => {
  const input = distributionInput.value
  const type = distributionType.value.id
  if (input.length) {
    // Match only the digits, whatever the separators are and even if separators inconsistant
    const matches = Array.from(input.matchAll(/(\d+)\D*/g))

    // Then for each set of matched digits
    const values = matches.map((match) => (
      // Parse the result of the first capture group as number
      parseInt(match[1], 10)
    ))

    switch (type) {
      case TYPE_START:
        // Sort the values (descending) so that they don't break our calculations
        // And reverse them since we need to start from the end
        values.sort((a, b) => b - a)
        break
      case TYPE_END:
        // Sort the values (ascending) so that they don't break our calculations
        values.sort((a, b) => a - b)
        break
      default:
    }

    return values
  }
  return []
})

const distributionSizes = computed(() => {
  const type = distributionType.value.id
  const values = distributionValues.value

  switch (type) {
    case TYPE_SIZE:
      return values
    case TYPE_START: {
      let end = 255
      return values.map((start) => {
        const size = (end - start) + 1
        end = start - 1
        return size
      }).reverse()
    }
    case TYPE_END: {
      let start = 0
      return values.map((end) => {
        const size = (end - start) + 1
        start = end + 1
        return size
      })
    }
    default:
      return []
  }
})

function distributionValidator(values) {
  const type = distributionType.value.id
  const errors = []
  if (presets.value.length !== values.length) {
    errors.push(`${translate('pb2.distributePresetsView.valuesCountNotMatchingError')} ${presets.value.length}`)
  }
  const last = values[values.length - 1]
  const duplicates = Tools.findDuplicates(values)
  switch (type) {
    case TYPE_START:
      // Using the last value because they are reversed for distribution from start
      if (last !== 0) {
        errors.push(translate('pb2.distributePresetsView.missingStartValue0Error'))
      }
      if (values[0] > 255) {
        errors.push(translate('pb2.distributePresetsView.startValueTooBigError'))
      }
      if (duplicates.length) {
        errors.push(translate('pb2.distributePresetsView.startValueNotUniqueError'))
      }
      break
    case TYPE_SIZE: {
      let occupied = 0
      let maxSize = 0
      let minSize = Infinity
      for (let i = 0; i < values.length; i += 1) {
        const current = values[i]
        occupied += current
        maxSize = Math.max(current, maxSize)
        minSize = Math.min(current, minSize)
      }
      if (minSize < 1) {
        errors.push(translate('pb2.distributePresetsView.sizeTooLowError'))
      }
      if (maxSize > 256) {
        errors.push(translate('pb2.distributePresetsView.sizeTooBigError'))
      }
      const free = 256 - occupied
      if (free > 0) {
        errors.push(translate('pb2.distributePresetsView.sizesSubTooLowError'))
        errors.push(`${translate('pb2.distributePresetsView.remainingSpaceError')}: ${free}`)
      }
      if (free < 0) {
        errors.push(translate('pb2.distributePresetsView.sizesSumTooBigError'))
        errors.push(`${translate('pb2.distributePresetsView.missingSpaceError')}: ${free * -1}`)
      }
    } break
    case TYPE_END:
      if (last !== 255) {
        errors.push(translate('pb2.distributePresetsView.missingEndValue255Error'))
      }
      if (last > 255) {
        errors.push(translate('pb2.distributePresetsView.endValueTooBigError'))
      }
      if (duplicates.length) {
        errors.push(translate('pb2.distributePresetsView.endValueNotUniqueError'))
      }
      break
    default:
  }
  return errors
}

const { isValid, error: errors, validate } = useValidator(distributionValidator, {
  value: distributionValues,
  immediate: true,
})

const disabled = computed(() => (
  // Disable distribution if the distribution input is shown and...
  showDistributionInput.value && (
    // the input is empty
    !distributionInput.value
    // the value of the input is not valid
    || !isValid.value
  )
))

const { isGlobal } = useIsGlobal()

const loading = usePresetRangeStatus().pending

const object = useObject()

const closeTo = computed(() => {
  const currentRoute = routerStore.route
  const { profileUid } = profileStore
  const currentObject = object.value
  if (loading.value) {
    return currentRoute
  }
  return currentObject ? currentObject.link : {
    name: routeNames.GlobalEditor,
    params: {
      profileUid,
    },
  }
})

async function distribute() {
  if (!showDistributionInput.value || validate()) {
    if (isGlobal.value) {
      await presetRangeStore.distributeGlobal((
        distributionSizes.value.length ? { distribution: distributionSizes.value } : {}
      ))
    } else {
      await presetRangeStore.distributeLocal((
        distributionSizes.value.length ? { distribution: distributionSizes.value } : {}
      ))
    }
    await routerStore.push(closeTo.value)
  }
}

const enterHotkeyEnabled = computed(() => !loading.value)

const { active: enterPressed } = useHotkeys(KEYS.Enter, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  exact: true,
  enabled: enterHotkeyEnabled,
})

watch(enterPressed, () => {
  if (enterPressed.value) {
    distribute()
  }
})
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '@nicolaudie-group/ng-ui-kit/components/UiField/field.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-presets-distribution-errors {
  margin-bottom: pb.$spacing;

  .pb-presets-distribution-errors-list {
    padding-left: pb.$half-spacing + pb.$quarter-spacing;
  }
}

.pb-presets-distribution-input-info {
  // Negate padding of field since we added padding on the icon to increase hoverable area
  @include field.cancel-v-spacing;
  @include field.cancel-h-spacing;
  @include field.add-v-padding;
  @include field.add-h-padding;
}
</style>
